import React, { useState, useEffect } from 'react';
import HeaderDashboard from './HeaderDashboard'; // Import Header component
import Sidebar from './Sidebar'; // Import Sidebar component
import './PatientHistory.css'; // Ensure you create this CSS file for styling
import './Dashboard.css'; // Ensure you create this CSS file for styling
import { faChevronRight, faTrash } from '@fortawesome/free-solid-svg-icons'; // Import right arrow icon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {axiosInstance} from './helper/axiosConfig';
import { Link, useLocation } from 'react-router-dom'; // Import Link and useLocation for navigatio
import { faCalendarAlt, faCalendarDay, faRedo, faClock } from '@fortawesome/free-solid-svg-icons';
import { isAdmin, isDoctor } from './helper/authRoles';

// Function to generate random colors for avatars
const getRandomColor = () => {
  const colors = ['#f56565', '#68d391', '#63b3ed', '#ed8936', '#f6ad55', '#9f7aea', '#48bb78', '#4299e1'];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Utility function to get the first letter of the patient's name
const getAvatarLetter = (name) => {
  return name ? name.charAt(0).toUpperCase() : '?';
};


const renderDxTags = (dx) => {
  if (!dx) {
    // If dx is null, undefined, or an empty string, return null or an empty element
    return <span className="tag">No data</span>;
  }
  const tags = dx.split(", ");

  // Only show the first 3 tags, followed by "..." if there are more
  const displayedTags = tags.slice(0, 3).map((tag, index) => (
    <span key={index} className="tag">
      {tag}
    </span>
  ));

  return (
    <>
      {displayedTags}
      {tags.length > 3 && <span className="tag">...</span>}
    </>
  );
};

function Dashboard() {
  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8; // Set rows per page for pagination
  const [totalPages, setTotalPages] = useState(1); // Track total pages
  const [searchTerm, setSearchTerm] = useState(''); // State for search term

  const currentDate = new Date();
  const tenYearsAgo = new Date(currentDate.setFullYear(currentDate.getFullYear() - 10)); // Calculate the date 10 years ago

  const [dateRange, setDateRange] = useState({
    startDate: tenYearsAgo,
    endDate: new Date(),
  });
  // New state variables for summary boxes
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [todaysAppointments, setTodaysAppointments] = useState(0);
  const [totalFollowUps, setTotalFollowUps] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState(0);
  const [fromDate, setFromDate] = useState(null);  // or use "" as an alternative
  const [todayDate, setTodayDate] = useState(null);

  function formatDate(dateString) {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear();

      // Determine the correct ordinal suffix
      let suffix = 'th';
      if (day === 1 || day === 21 || day === 31) suffix = 'st';
      else if (day === 2 || day === 22) suffix = 'nd';
      else if (day === 3 || day === 23) suffix = 'rd';

      return `${day}${suffix} ${month} ${year}`;
  }



  const fetchSummaryData = async () => {
    try {
      const response = await axiosInstance.get('/api/report/summary'); // Adjust endpoint
      setTotalAppointments(response.data.total_appointments);
      setTodaysAppointments(response.data.todays_appointments);
      setTotalFollowUps(response.data.total_followups);
      setPendingAppointments(response.data.total_pending_appointments);
      setFromDate(formatDate(response.data.from_date));
      setTodayDate(formatDate(response.data.today));
    } catch (error) {
      console.error('Error fetching summary data:', error);
    }
  };


  // Fetch patient history data with pagination and filtering
  const fetchPrescriptionHistory = async (page, searchTerm, dateRange) => {
    try {
      const response = await axiosInstance.get(`/api/prescription-all/`, {
        params: {
          page: page,
          limit: rowsPerPage,
          search: searchTerm,
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString(),
        },
      });

      const prescriptions = response.data.results.map((prescription) => {
        const createdAt = new Date(prescription.created_at);

        // Helper function to add ordinal suffix to the day
        const getOrdinalSuffix = (day) => {
          if (day > 3 && day < 21) return "th";
          switch (day % 10) {
            case 1: return "st";
            case 2: return "nd";
            case 3: return "rd";
            default: return "th";
          }
        };

        const day = createdAt.getDate();
        const month = createdAt.toLocaleString('default', { month: 'short' });  // e.g., "Sept"
        const year = createdAt.getFullYear();

        return {
          ...prescription,
          date: `${day}${getOrdinalSuffix(day)} ${month}. ${year}`,  // Format date as "28th Sept. 2024"
          time: createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })  // Format time (e.g., "03:30 PM")
        };
      });

      setPrescriptionHistory(prescriptions);
      setTotalPages(Math.ceil(response.data.total / rowsPerPage)); // Assuming the API returns a `total` count of patients
    } catch (error) {
      console.error('Error fetching prescription history:', error);
      setError('Failed to fetch prescription history.');
    } finally {
      setLoading(false); // Stop the loading state
    }
  };

  useEffect(() => {
    fetchSummaryData();
    fetchPrescriptionHistory(currentPage, searchTerm, dateRange);
  }, [currentPage, searchTerm, dateRange]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  // New function to handle search and date changes
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleDateChange = (ranges) => {
    setDateRange({
      startDate: ranges.startDate,
      endDate: ranges.endDate,
    });
    setCurrentPage(1); // Reset to first page on date change
  };

  return (
    <div className="dashboard-container">
      <Sidebar /> {/* Sidebar component */}
      <div className="dashboard-main">
        <HeaderDashboard onSearch={handleSearch} />

      <div className="summary-container">
        <div className="summary-box box-one">
          <div className="summary-content">
            <h2>{totalAppointments}</h2>
            <p>Total Appointments</p>
            <span>{fromDate} - {todayDate}</span>
          </div>
          <div className="summary-icon">
            <FontAwesomeIcon icon={faCalendarAlt} />
          </div>
        </div>
        <div className="summary-box box-two">
          <div className="summary-content">
            <h2>{todaysAppointments}</h2>
            <p>Today's Appointments</p>
            <span>{todayDate}</span>
          </div>
          <div className="summary-icon">
            <FontAwesomeIcon icon={faCalendarDay} />
          </div>
        </div>
        <div className="summary-box box-three">
          <div className="summary-content">
            <h2>{totalFollowUps}</h2>
            <p>Total Follow Ups</p>
            <span>{fromDate} - {todayDate}</span>
          </div>
          <div className="summary-icon">
            <FontAwesomeIcon icon={faRedo} />
          </div>
        </div>
        <div className="summary-box box-four" >
          <div className="summary-content">
            <h2>{pendingAppointments}</h2>
            <p>Total Pending Appointments</p>
            <span>{fromDate} - {todayDate}</span>
          </div>
          <div className="summary-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
        </div>
      </div>

        <div className="content-box bashboard-content">
          <div className="patient-history-container">
          <div className="header-container">
              <h1>Recent Created Prescription</h1>
              {isDoctor() && (
                <button className="btn print-btn dasboard-create-btn">
                  <Link to="/create-prescription/"> + Create Prescription</Link>
                </button>
              )}
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <>
              {prescriptionHistory && prescriptionHistory.length > 0 && (
                <>
                <table className="patient-history-table">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Mobile</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>History</th>
                      <th>Prescription Id</th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescriptionHistory.map((prescription) => (
                      <tr key={prescription.id}>
                        <td>
                          {/* Avatar circle
                          // <div
                          //   className="avatar-circle"
                          //   style={{ backgroundColor: getRandomColor() }}
                          // >
                          //   <span className="initials">{getAvatarLetter(prescription.patient.name)}</span>
                          // </div>
                          */}
                          <div className="avatar-circle" style={{ backgroundColor: !prescription.patient.profile_pic ? getRandomColor() : 'transparent' }}>
                            {prescription && prescription.patient && prescription.patient.profile_pic ? (
                              <img
                                    src={`${process.env.REACT_APP_AXIOS_BASE_URL}${prescription.patient.profile_pic}`} // Concatenating the base URL and profile_pic
                                    alt={prescription.patient.full_name} className="avatar-circle"
                                  // Ensure the profile-pic class is styled in your CSS
                                  />

                            ) : (
                              <span className="initials">{getAvatarLetter(prescription.patient.name)}</span>
                            )}
                          </div>

                            {prescription.patient.name}
                        </td>
                        <td>{prescription.patient.mobile}</td>
                        <td>{prescription.date}</td>
                        <td>{prescription.time}</td>
                        <td>
                          {renderDxTags(prescription.relevant_history)}
                        </td>
                        <td>
                          #{prescription.id}
                        </td>
                        <td>
                          <Link to={`/prescription-preview/${prescription.id}`}>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="picker-icon"
                            />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination">
                  {totalPages > rowsPerPage && (
                    <>
                      {currentPage > 1 && (
                        <button onClick={() => handlePageChange(currentPage - 1)} className="ltgt">&lt;</button>
                      )}
                      {Array.from({ length: totalPages }, (_, index) => {
                        if (index + 1 === currentPage || index + 1 === 1 || index + 1 === totalPages || Math.abs(currentPage - (index + 1)) <= 1) {
                          return (
                            <button
                              key={`page-${index + 1}`} // Use a unique key for each page button
                              onClick={() => handlePageChange(index + 1)}
                              className={currentPage === index + 1 ? 'active' : ''}
                            >
                              {index + 1}
                            </button>
                          );
                        } else if (Math.abs(currentPage - (index + 1)) === 2) {
                          return <span key={`dots-${index}`} className="dots">...</span>; // Unique key for each "..."
                        }
                        return null;
                      })}
                      {currentPage < totalPages && (
                        <button onClick={() => handlePageChange(currentPage + 1)} className="ltgt">&gt;</button>
                      )}
                    </>
                  )}
                </div>
                </>
              )}
              {prescriptionHistory && prescriptionHistory.length === 0 && (
                <p>No prescriptions found.</p>
              )}
              </>
            )}
          </div>
        </div>
        <div className="footer-content">
            {process.env.REACT_APP_COPYRIGHT_TEXT || "© 2025, Powered by AmarLab"}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
